import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteSticker, fetchStickers, saveSticker } from '../../services/apiContacts';

/**
 * Selector de stickers del compositor del chat.
 *
 * Los más usados van primero y ese orden lo da el uso real, no una
 * configuración: la biblioteca se sembró con el historial del chat, así que
 * los que el equipo ya manda aparecen arriba desde el primer día.
 */
export default function StickerPicker({ onPick, onClose, enviando }) {
    const queryClient = useQueryClient();
    const inputArchivo = useRef(null);
    const [error, setError] = useState(null);

    const { data: stickers = [], isLoading } = useQuery({
        queryKey: ['stickers'],
        queryFn: fetchStickers,
        staleTime: 5 * 60 * 1000,
    });

    const subir = useMutation({
        mutationFn: saveSticker,
        onSuccess: () => {
            setError(null);
            queryClient.invalidateQueries({ queryKey: ['stickers'] });
        },
        onError: (e) => setError(e?.response?.data?.message || 'No se pudo guardar el sticker.'),
    });

    const quitar = useMutation({
        mutationFn: deleteSticker,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stickers'] }),
    });

    return (
        <div className="absolute bottom-[calc(100%+12px)] left-0 w-80 bg-white dark:bg-[#233138] rounded-2xl shadow-[0_-8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_-8px_30px_rgb(0,0,0,0.4)] border border-gray-100 dark:border-gray-700/50 overflow-hidden z-[100]">
            <div className="p-3 border-b border-gray-50 dark:border-gray-700/50 bg-gray-50/80 dark:bg-gray-800/80 flex items-center justify-between">
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Stickers</span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => inputArchivo.current?.click()}
                        className="text-gray-400 hover:text-primary p-1"
                        title="Agregar un sticker (.webp)"
                    >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                    </button>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" title="Cerrar">
                        <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                </div>
            </div>

            <input
                ref={inputArchivo}
                type="file"
                accept="image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) subir.mutate({ file });
                    e.target.value = '';
                }}
            />

            {error && (
                <div className="px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20">
                    {error}
                </div>
            )}

            <div className="max-h-[260px] overflow-y-auto no-scrollbar p-2">
                {isLoading && <p className="text-xs text-gray-400 p-3 text-center">Cargando…</p>}

                {!isLoading && stickers.length === 0 && (
                    <p className="text-xs text-gray-400 p-4 text-center leading-relaxed">
                        Todavía no hay stickers.<br />
                        Agregá uno con <b>+</b>, o guardá los que ya usás desde el chat.
                    </p>
                )}

                <div className="grid grid-cols-4 gap-1">
                    {stickers.map((s) => (
                        <div key={s.id} className="relative group">
                            <button
                                type="button"
                                disabled={enviando}
                                onClick={() => onPick(s)}
                                title={s.label || `Usado ${s.times_used} ${s.times_used === 1 ? 'vez' : 'veces'}`}
                                className="w-full aspect-square p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition disabled:opacity-40"
                            >
                                <img src={s.url} alt={s.label || 'Sticker'} className="w-full h-full object-contain" />
                            </button>
                            <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('¿Quitar este sticker de la biblioteca?')) quitar.mutate(s.id);
                                }}
                                className="absolute -top-1 -right-1 hidden group-hover:flex items-center justify-center w-4 h-4 rounded-full bg-gray-700 text-white cursor-pointer"
                                title="Quitar de la biblioteca"
                            >
                                <span className="material-symbols-outlined text-[10px]">close</span>
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
